import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { defineMessages } from 'react-intl';
import type SuccessToast from '@alltrails/shared/types/successToast';
import { UserConnectionActionType } from '@alltrails/types/belay';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import type { UserId } from '@alltrails/types/User';
import { getRelationshipAction } from '@alltrails/shared/utils/community/analyticsHelpers';
import logCommunityConnectActionSelected from '@alltrails/amplitude/events/logCommunityConnectActionSelected';
import CommunityConnectActionLocation from '@alltrails/amplitude/enums/CommunityConnectActionLocation';
import LoadingDots from '@alltrails/denali/components/LoadingDots';
import useUserConnection from '@alltrails/social-connections/hooks/useUserConnection';
import ConnectButton from '@alltrails/social-connections/components/ConnectButton';
import styles from './styles/styles.module.scss';

type BlockUserMenuOptionProps = {
  currentUserId: number;
  blockUserId: UserId | string;
  setSuccessToast: Dispatch<SetStateAction<SuccessToast>>;
  className?: string;
  customTitle?: boolean;
  analyticsLocation?: CommunityConnectActionLocation;
  onMenuClick?: () => void;
  blockCallback?: () => void;
};

const messages = defineMessages({
  blockUserText: { defaultMessage: 'Block user' },
  unblockUserText: { defaultMessage: 'Unblock user' }
});

export default function BlockUserMenuOption({
  currentUserId,
  blockUserId,
  setSuccessToast,
  className,
  customTitle,
  analyticsLocation,
  onMenuClick,
  blockCallback
}: BlockUserMenuOptionProps) {
  const {
    formattedDefaultMessages: { blockUserText, unblockUserText }
  } = useFormatMessage(messages);
  const userConnection = useUserConnection(currentUserId as number, blockUserId as number);
  const blockActionType = useMemo(() => {
    const actionsTypes = userConnection?.actions ? Object.keys(userConnection?.actions) : [];
    return [UserConnectionActionType.Unblock, UserConnectionActionType.Block].find(action => actionsTypes.includes(action));
  }, [userConnection]);

  const buttonText = () => {
    if (blockActionType === UserConnectionActionType.Unblock) {
      return unblockUserText;
    }
    return blockUserText;
  };

  const logConnectActionSelected = useCallback(
    (actionType: UserConnectionActionType) => {
      const relationshipAction = getRelationshipAction(actionType);
      if (currentUserId && blockUserId && analyticsLocation) {
        logCommunityConnectActionSelected({
          relationship_action: relationshipAction,
          carousel_position: -1,
          item_location: analyticsLocation,
          item_position: 0,
          source_user_id: String(currentUserId),
          target_user_id: String(blockUserId)
        });
      }
    },
    [currentUserId, blockUserId, analyticsLocation]
  );

  if (!userConnection) {
    return <LoadingDots />;
  }

  return (
    <ConnectButton
      className={classNames(className, styles.blockSection)}
      onClick={logConnectActionSelected}
      actionType={blockActionType}
      userConnection={userConnection}
      stopPropagation
      title={customTitle ? buttonText() : undefined}
      setSuccessToast={setSuccessToast}
      onMenuClick={onMenuClick}
      callback={blockCallback}
    />
  );
}
